import React from "react";
import { Button, Card, Table } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useStockReport } from "../../../../data/hooks/firestore/useStockReport";
import { formatWeight } from "../../../../functions/formatters";
import utils from "../../../../utils";
import * as XLSX from "xlsx";

const StockReport = ({ dateRange, dateRangeText, companyFilter, loading: parentLoading }) => {
  const { t } = useTranslation();
  const { products, getProductById } = useProducts();
  const { getAggregatedAddedStock, getStockDataForExport, loading: stockLoading } = useStockReport(dateRange);

  // Calculate weight for a product (including bundle products)
  const calculateProductWeight = (productDetails) => {
    if (!productDetails) return 0;

    // If it's a bundle, calculate total weight from bundleItems and packagingMaterial
    if (productDetails.isBundle) {
      let totalBundleWeight = 0;

      // Add weight from bundle items
      if (productDetails.bundleItems && Array.isArray(productDetails.bundleItems)) {
        productDetails.bundleItems.forEach((bundleItem) => {
          const itemProduct = getProductById(bundleItem.productId);
          const itemWeight = itemProduct?.weight || 0;
          totalBundleWeight += itemWeight * (bundleItem.quantity || 1);
        });
      }

      // Add weight from packaging material
      if (productDetails.packagingMaterial?.productId) {
        const packagingProduct = getProductById(productDetails.packagingMaterial.productId);
        const packagingWeight = packagingProduct?.weight || 0;
        totalBundleWeight += packagingWeight * (productDetails.packagingMaterial.quantity || 1);
      }

      return totalBundleWeight;
    }

    // Regular product - return weight directly
    return productDetails.weight || 0;
  };

  // Get packed products and add weight information
  const allPackedProducts = getAggregatedAddedStock(companyFilter, products).map((product) => {
    const productDetails = getProductById(product.productId);
    const productWeight = calculateProductWeight(productDetails);
    return {
      ...product,
      weight: productWeight,
      totalWeight: product.totalAddedStock * productWeight,
    };
  });

  const handleExportExcel = () => {
    const exportData = getStockDataForExport(companyFilter, products);

    if (!exportData.products.length) {
      return;
    }

    // Build worksheet data
    const wsData = [];

    // Row 1: empty
    wsData.push([]);

    // Row 2: headers - empty, Product, Totaal, Total Weight, dates...
    const headerRow = ["", t("product_name"), t("total_packed"), `${t("total_weight")} (g)`, ...exportData.dates];
    wsData.push(headerRow);

    // Row 3+: product data
    exportData.products.forEach((product) => {
      const productDetails = getProductById(product.productId);
      const productWeight = calculateProductWeight(productDetails);
      const totalWeight = product.total * productWeight;

      const row = [
        "", // Column A empty
        product.productName,
        product.total,
        totalWeight,
        ...exportData.dates.map((date) => product.stockByDate[date] || ""),
      ];
      wsData.push(row);
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_array ? XLSX.utils.aoa_to_sheet(wsData) : XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws["!cols"] = [
      { wch: 3 }, // A: empty
      { wch: 30 }, // B: product name
      { wch: 12 }, // C: totals
      { wch: 15 }, // D: total weight
      ...exportData.dates.map(() => ({ wch: 12 })), // E+: dates
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");

    // Generate filename with date range
    const filename = `stock-report-${dateRangeText?.replace(/\s/g, "-") || "export"}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const columns = [
    {
      title: t("product_name"),
      dataIndex: "productName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
    },
    {
      title: t("total_packed"),
      dataIndex: "totalAddedStock",
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalAddedStock"),
    },
    {
      title: t("total_weight"),
      dataIndex: "totalWeight",
      render: (weight) => formatWeight(weight),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalWeight"),
    },
  ];

  return (
    <Card
      extra={
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportExcel}
          disabled={!allPackedProducts.length || stockLoading}
        >
          {t("export")}
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <span className="text-muted">{dateRangeText}</span>
      </div>
      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={columns}
          dataSource={allPackedProducts}
          rowKey="productId"
          loading={parentLoading || stockLoading}
          pagination={{ showSizeChanger: true }}
          scroll={{ x: 400 }}
        />
      </div>
    </Card>
  );
};

export default StockReport;
